"use client";

import React, { useEffect, useState } from "react";
import { useAuthStep } from "@/hooks/useAuthStep";
import DOB from "@/components/DOB";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { checkTrialClaimed, getAgeBand, setAgeBand } from "@/lib/utils";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { CLOUDFLARE_URL } from "@/lib/config";

const formSchema = z.object({
    month: z.string().min(1, "Please select a month"),
    year: z.string().min(1, "Please select a year"),
});

const AgeVerification = () => {
    const { setAuthStep, token, previouslyAppliedTheme, postReapplyHandle, loginState, handleAuthFinish } = useAuthStep();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    const { authenticatedFetch, getOptions } = useCommunication();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            month: "",
            year: "",
        },
    });

    const isDOBValid = () => {
        const formData = form.getValues();
        const month = formData.month;
        const year = formData.year;

        if (!month || !year) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        const subscription = form.watch((value) => {
            setIsButtonDisabled(!isDOBValid());
        });
        return () => subscription.unsubscribe();
    }, [form]);

    const verifyAge = async (data: any) => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const birthYear = parseInt(data.year);
        const birthMonth = parseInt(data.month);

        // Check if user is under 13
        if (currentYear - birthYear < 13 ||
            (currentYear - birthYear === 13 && currentMonth < birthMonth)) {
            setAuthStep("underAge");
            return;
        }

        await fetch(`${CLOUDFLARE_URL}/users/verify-age`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token?.access_token}`
            },
            body: JSON.stringify({
                "year": birthYear
            }),
        });

        if (previouslyAppliedTheme) {
            setAuthStep("previouslyAppliedTheme");
        } else {
            handleAuthFinish();
        }
    }

    return (
        <div className="px-[30px] text-gray-500">
            <h1 className="text-2xl font-semibold text-black mb-[5px]">Age Verification Required</h1>
            <p>We need to verify your age before you can continue.</p>
            <div className="mt-[20px]">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit((data) => verifyAge(data))}>
                        <DOB form={form} />
                        <div className={`mt-4 ${isButtonDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                            <button
                                type="submit"
                                className="w-full flex items-center justify-center gap-2 rounded-md border border-gray-300 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Continue
                            </button>
                        </div>
                    </form>
                </Form>
            </div>
        </div>
    );
};

export default AgeVerification;